import React from 'react';
import { useTranslation } from 'react-i18next';
import TodoList, { type TodoListItem } from './TodoList';
import type { PersonTodo, PersonTodoKind } from '../utils/personTodos';

export interface PersonTodoRow extends PersonTodo {
  /** Where the row's action button leads (deep link to the thing to act on). */
  href: string;
}

interface PersonTodoListProps {
  items: PersonTodoRow[];
  /** Rows shown before "Visa fler". */
  maxVisible?: number;
}

// Feather icon per item — reuses the platform's existing icon vocabulary.
const TODO_ICON: Record<PersonTodoKind, string> = {
  payment: 'credit-card',
  approveMilestone: 'check-circle',
  objection: 'alert-triangle',
  conflict: 'alert-octagon',
  signContract: 'edit-3',
  reviewContract: 'file-text',
  completeProfile: 'user',
};

// A revised contract has its own copy.
const copyKey = (item: PersonTodoRow) => item.revised ? 'reviewRevisedContract' : item.kind;

/** Maps the homeowner's active to-dos onto the shared "Att göra nu" list:
 * one row per concrete contract/milestone/payment, with the copy filled in
 * from that item. */
export default function PersonTodoList({ items, maxVisible }: PersonTodoListProps) {
  const { t, i18n } = useTranslation();
  const formatAmount = (ore: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(ore / 100);

  const rows: TodoListItem[] = items.map(item => {
    const vars = {
      project: item.projectDescription ?? '',
      company: item.companyName ?? '',
      contract: item.contractName ?? '',
      contractNumber: item.contractNumber ?? '',
      milestone: item.milestoneDescription ?? '',
      milestoneNumber: item.milestoneNumber ?? '',
      amount: item.amountOre != null ? formatAmount(item.amountOre) : '',
    };
    return {
      key: item.key,
      priority: item.priority,
      icon: TODO_ICON[item.kind],
      title: t(`personHome.todoNow.items.${copyKey(item)}.title`, vars),
      // Which project (and contract) the row is about, shown even while the
      // explanation is folded.
      context: item.projectDescription
        ? t(item.contractNumber ? 'personHome.todoNow.context' : 'personHome.todoNow.contextProject', vars)
        : undefined,
      description: item.kind === 'signContract' && item.objectionMilestoneNumber != null
        ? t('personHome.todoNow.items.signContract.descObjection', { ...vars, objectionMilestoneNumber: item.objectionMilestoneNumber })
        : t(`personHome.todoNow.items.${copyKey(item)}.desc`, vars),
      note: item.paymentPending ? t('fundingNotice.pending') : undefined,
      // Review/sign in "Att göra nu" shares its label with the "Granska
      // Kontrakt" widget button: same action, same name.
      actionLabel: item.kind === 'signContract' || item.revised
        ? t('contracts.actions.reviewAndSign')
        : t(`personHome.todoNow.items.${item.kind}.action`),
      href: item.href,
      testId: `person-todo-${item.kind}`,
    };
  });

  return <TodoList items={rows} maxVisible={maxVisible} emptyTestId="person-todo-empty" />;
}
